import React, {
  ReactElement,
  useState,
  useContext,
  useMemo,
  useEffect,
  memo,
} from 'react'
import {
  TableColumn,
  TextBody,
  get,
  Box,
  Button,
  Spinner,
  Icon,
  useMediaSize,
  Alert,
} from '@ally/metronome-ui'
import { muiCheckFront } from '@ally/metronome-icons'
import { Private } from '@ally/private'
import styled from 'styled-components'
import {
  TransactionHistoryTableModel,
  SearchResults,
  NextRequestResponse,
  CheckDetails,
} from '../../../../types'
import { useTransactionHistoryApi } from '../../../../api/transaction-history-api'
import {
  formatSearchedTransactions,
  mapTransactions,
} from '../../../../utils/transactionMapper'
import { TransactionHistoryContext } from '../../../../services'
import { LastRowBox } from './LastRowBox'
import { MobileCardList } from '../../MobileCardList'
import { TransactionHistoryMobileCard } from './TransactionHistoryMobileCard'
import { FormattedAmountString } from '../../FormattedAmountString'
import {
  StyledTable,
  StyledTextBody,
} from '../../../../utils/commonStyledComponents'
import { TransactionDetailModal } from './TransactionDetailModal'

const TableBox = styled(Box)`
  border-bottom: none ${get.media.up('lg')} {
    border-bottom: solid ${get.colors('slate-8')};
  }
  @media print {
    display: table;
    width: 100%;
  }
`

const StyledCheckIcon = styled(Icon)`
  margin-right: ${get.space('xs')};
`

export const NonPrintingWrapper = styled(Box)`
  @media print {
    display: none;
  }
`

const StyledTextBodyColorSky = styled(TextBody)`
  color: ${get.colors('sky-4')};
`

export const FlexedButton = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  white-space: break-spaces;
  text-align: left;
  @media print {
    white-space: normal;
    word-wrap: break-word;
    width: 300px;
    text-align: left;
  }
`

const PrintTransactionCounter = styled(Box)`
  display: none;
  @media print {
    display: block;
    text-align: center;
  }
`
const StyledAlert = styled(Alert)`
  padding: ${get.space('xs')};
  svg {
    width: 20px;
    height: 20px;
  }
  > div > span {
    font-size: ${get.fontSizes('md')};
  }
`

export const TransactionHistoryTable: React.FC<{
  searchResults: SearchResults[] | null
  searchError: boolean
  printCheckCallback: (data: CheckDetails) => void
}> = memo(({ searchResults, searchError, printCheckCallback }) => {
  const isLgDown = useMediaSize('LgDown')
  const { fetchTransactionHistory } = useTransactionHistoryApi()
  const [selectedTransactionDetails, setSelectedTransactionDetails] =
    useState<TransactionHistoryTableModel>()
  const [showModal, setShowModal] = useState(false)
  // provider states
  const {
    transactionHistoryState: { data, isLoading, hasError, update },
  } = useContext(TransactionHistoryContext)
  // local states
  const [nextRequest, setNextRequest] = useState<NextRequestResponse>()
  // view more button states
  const [viewMoreLoading, setLoading] = useState(false)
  const [viewMoreError, setError] = useState(false)

  const postedTransactions =
    data &&
    data[1] &&
    data[1].transactionHistory &&
    data[1].transactionHistory[0].transactions
  const pendingTransactions =
    data &&
    data[0] &&
    data[0].transactionHistory &&
    data[0].transactionHistory[0].transactions
  const currentTransactionAmount = postedTransactions?.length
  const totalRecords =
    data &&
    data[1] &&
    data[1].transactionHistory &&
    data[1].transactionHistory[0].matchedRecordsTotal

  useEffect(() => {
    if (data && data[1] && data[1].transactionHistory) {
      // Setting nextRequest which will be used for next call
      setNextRequest(data[1].transactionHistory[0].nextPageRequestDetails)
    }
  }, [data])

  const handleMoreData = async (): Promise<any> => {
    // onClick function for View More button. The purpose of this function
    // is to update the transaction History state with the new fetched data.
    setLoading(true)
    try {
      const response = await fetchTransactionHistory(
        JSON.stringify(nextRequest),
      )
      const [posted] = response.searchResults
      // adding in previously posted transactions to response
      // this way the correct nextResponse details are carried over
      /* istanbul ignore else */
      if (postedTransactions) {
        posted.transactionHistory[0].transactions.unshift(...postedTransactions)
      }

      // adding total record amount to new posted response
      posted.transactionHistory[0].matchedRecordsTotal = totalRecords
      /* istanbul ignore next */ // data[0] (the pending obj) will be defined at this point
      update({
        data: [data?.[0] || {}, posted],
      })
    } catch (error) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const lastRow = (): React.ReactElement | undefined => {
    // service error from search call or sts call
    if (hasError) {
      return (
        <LastRowBox alignment="center">
          <StyledAlert
            variant="error"
            body="We can't load your transaction history at this time. Try again
            later."
            disableContainer
          />
        </LastRowBox>
      )
    }
    if (searchError) {
      return (
        <LastRowBox alignment="center">
          <Alert
            variant="error"
            body="We're unable to process your request at this time. Please call
            us 24/7 at 1-877-247-2559 for help."
            disableContainer
          />
        </LastRowBox>
      )
    }
    // no lastRow for searched transactions
    if (searchResults && searchResults.length > 0) {
      return undefined
    }
    // no transactions from search
    if (searchResults && searchResults.length === 0) {
      return (
        <LastRowBox alignment="left" data-testid="noSearchTransactionsError">
          <TextBody data-testid="no-search-transactions" size="sm" tag="span">
            <strong>We couldn&apos;t find a match for your search.</strong> We
            look for keywords in transaction descriptions and can&apos;t search
            for amounts. Try again.
          </TextBody>
        </LastRowBox>
      )
    }

    // table error if no transactions
    if (data && data[0] && data[1]) {
      if (
        /* istanbul ignore next */
        !postedTransactions?.length &&
        /* istanbul ignore next */
        !pendingTransactions?.length &&
        !isLoading
      ) {
        return (
          <LastRowBox alignment="center">
            <TextBody size="md" tag="span" data-testid="noTransactionsMessage">
              You don&apos;t currently have any transactions.
            </TextBody>
          </LastRowBox>
        )
      }
    }

    // view more button states
    if (nextRequest && nextRequest.recordsToPull > 0) {
      if (viewMoreLoading) {
        return (
          <LastRowBox alignment="center">
            <Spinner size="small" />
          </LastRowBox>
        )
      }
      if (viewMoreError) {
        return (
          <LastRowBox alignment="center">
            <Alert
              body="We can't load more transactions at this time. Try again
              later."
              variant="error"
              disableContainer
            />
          </LastRowBox>
        )
      }
      /* istanbul ignore else */
      if (!viewMoreError && !viewMoreLoading) {
        return (
          <LastRowBox alignment="center" data-testid="viewMore">
            <NonPrintingWrapper>
              <Button
                data-testid="viewMoreButton"
                variant="link"
                onClick={handleMoreData}
                unsupportedContent={
                  <StyledTextBodyColorSky weight="bold" size="sm" tag="span">
                    View More
                  </StyledTextBodyColorSky>
                }
                withAllyTM="transactionHistory-viewMore"
              />
            </NonPrintingWrapper>
          </LastRowBox>
        )
      }
    }
    return undefined
  }

  const tableData: TransactionHistoryTableModel[] = useMemo(() => {
    // if there are search results, format those and show in table
    if (searchResults && searchResults.length > 0) {
      return formatSearchedTransactions(searchResults)
    }

    // return no table data when there is a search error or no transactions
    if ((searchResults && searchResults.length === 0) || searchError) {
      return []
    }

    // return mapped transactions as usual.
    // this is covered in existing tests
    /* istanbul ignore next */
    if (data) {
      return mapTransactions(data)
    }

    /* istanbul ignore next */
    return []
  }, [data, searchResults, searchError, hasError])

  const handleDescriptionClick = (selectedTransaction: string): void => {
    const transactionDetails = tableData.find(
      transaction => transaction.id === selectedTransaction,
    )
    // istanbul ignore else
    if (transactionDetails) {
      setSelectedTransactionDetails({ ...transactionDetails })
      setShowModal(true)
    }
  }

  const TransactionHistoryColumns: TableColumn<TransactionHistoryTableModel>[] =
    [
      {
        Header: 'Date',
        accessor: 'date',
        alignment: 'left',
        Cell: ({
          cell: { value },
        }: {
          cell: { value: string }
        }): React.ReactElement => {
          return (
            <StyledTextBody tag="span" size="sm">
              {value}
            </StyledTextBody>
          )
        },
      },
      {
        id: 'descriptionRow',
        Header: 'Description',
        accessor: (
          row: TransactionHistoryTableModel,
        ): {
          id: string
          description: string
          isDepositedCheck?: boolean
          isCheck: boolean
          showEdd: boolean
        } => {
          return {
            id: row.id,
            description: row.description,
            isDepositedCheck: row.isDepositedCheck,
            isCheck: row.isCheck,
            showEdd: row.showEdd,
          }
        },
        alignment: 'left',
        Cell: ({
          cell: { value },
          isDrawerOpen,
          drawerTriggerRef,
        }: {
          cell: {
            value: TransactionHistoryTableModel
          }
          isCheck: boolean
          isDrawerOpen: boolean
          drawerTriggerRef: React.Ref<HTMLButtonElement> | undefined
        }): string | ReactElement => {
          return (
            <Private>
              <FlexedButton
                variant="link"
                unsupportedContent={
                  <>
                    {value.isCheck ? (
                      /* istanbul ignore next */
                      <StyledCheckIcon
                        icon={muiCheckFront}
                        size="xl"
                        customTitle="Check Transaction"
                      />
                    ) : null}
                    {value.description}
                  </>
                }
                onClick={
                  /* istanbul ignore next */ (): void => {
                    /* istanbul ignore next */
                    handleDescriptionClick(value.id)
                  }
                }
                ref={drawerTriggerRef}
                aria-expanded={
                  isDrawerOpen ? /* istanbul ignore next */ 'true' : 'false'
                }
                data-testid="transaction-history-desc"
                withAllyTM={`transactionHistory-desc-${value.description}`}
              />
              {value.isDepositedCheck && (
                <TextBody tag="p" size="sm" weight="normal">
                  Deposit pending approval
                </TextBody>
              )}
              {value.showEdd && (
                <TextBody tag="p" size="sm" weight="normal">
                  Early Direct Deposit
                  <span aria-hidden="true">💰</span>
                </TextBody>
              )}
            </Private>
          )
        },
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        alignment: 'right',
        Cell: ({
          cell: { value },
        }: {
          cell: {
            value: number
          }
        }): string | ReactElement => {
          if (typeof value === 'number') {
            return <FormattedAmountString value={value} />
          }
          return ''
        },
      },
      {
        Header: 'Balance',
        accessor: 'balance',
        alignment: 'right',
        Cell: ({
          cell: { value },
        }: {
          cell: {
            value: number
          }
        }): string | ReactElement => {
          if (typeof value === 'number') {
            return <FormattedAmountString value={value} />
          }
          return ''
        },
      },
    ]

  const mobileCards = tableData.map(
    (transaction: TransactionHistoryTableModel) => {
      return (
        <TransactionHistoryMobileCard
          data={transaction}
          printCheckCallback={printCheckCallback}
        />
      )
    },
  )

  const ViewMoreTable = (): React.ReactElement => {
    return (
      <Box>
        {isLgDown ? (
          <MobileCardList
            data={mobileCards}
            isLoading={isLoading}
            lastRow={!isLoading && lastRow()}
          />
        ) : (
          <>
            <StyledTable
              shared={{
                captionText: 'Transaction History',
                isLoading,
              }}
              table={{
                fullWidth: true,
                caption: { isVisible: false },
              }}
              data-testid="transactionHistoryTable"
              config={{
                data: tableData,
                columns: TransactionHistoryColumns,
              }}
            />

            <TransactionDetailModal
              data={selectedTransactionDetails}
              printCheckCallback={printCheckCallback}
              showModal={showModal}
              closeModal={(): void => setShowModal(false)}
            />

            {!isLoading && lastRow()}
          </>
        )}

        {!!currentTransactionAmount && !!totalRecords && !searchResults && (
          <PrintTransactionCounter>{`${currentTransactionAmount} of ${totalRecords} transactions`}</PrintTransactionCounter>
        )}
      </Box>
    )
  }

  return (
    <TableBox display="flex" flexDirection="column">
      <ViewMoreTable />
    </TableBox>
  )
})
